import React from "react";
import { Box, Typography } from "@mui/material";

import {
  InitiativeDetailCostEfficiencyIcon,
  InitiativeDetailRealtimeIcon,
} from "assets";
import { formatCurrency, formatNullable } from "utils";
import { InfoTooltip } from "components/info-tooltip";
import { CALCULATED_FALLBACK } from "components/metric-card";
import { COST_EFFICIENCY_TOOLTIPS } from "./consts";
import { CostEfficiencyCardProps } from "./types";

export const CostEfficiencyCard: React.FC<CostEfficiencyCardProps> = ({
  derived,
  unit,
}) => (
  <Box
    sx={{
      background:
        "linear-gradient(135deg, #1A4252 0%, #0F2A36 50%, #1A4252 100%)",
      borderRadius: "12px",
      p: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 3,
      flexWrap: "wrap",
      boxShadow: "0 8px 32px rgba(15, 42, 54, 0.45), 0 2px 8px rgba(0,0,0,0.2)",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: "32px", flex: 1 }}>
      <InitiativeDetailCostEfficiencyIcon
        width={72}
        height={72}
        style={{ flexShrink: 0 }}
      />
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "1.125rem",
              fontWeight: 800,
              color: "white",
              mb: 0.5,
            }}
          >
            Cost Efficiency Metric
          </Typography>
          <InfoTooltip
            tooltip={COST_EFFICIENCY_TOOLTIPS}
            colors={CALCULATED_FALLBACK}
            iconColor="white"
          />
        </Box>
        <Typography
          sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", mb: 1 }}
        >
          Auto-calculated: CapEx ÷ Expected Annual Improvement
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            backgroundColor: "rgba(255,255,255,0.1)",
            borderRadius: "8px",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          <InitiativeDetailRealtimeIcon width={9} height={10} />
          <Typography
            sx={{ fontSize: "0.6875rem", fontWeight: 700, color: "white" }}
          >
            Real-time calculation
          </Typography>
        </Box>
      </Box>
    </Box>
    <Box
      sx={{
        textAlign: "center",
        backgroundColor: "rgba(255,255,255,0.08)",
        borderRadius: "16px",
        px: 3,
        py: 2.5,
        border: "1px solid rgba(255,255,255,0.12)",
        minWidth: 180,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 700,
          color: "rgba(255,255,255,0.6)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          mb: 1,
        }}
      >
        Cost per KPI Unit
      </Typography>
      <Typography
        sx={{
          fontSize: "2.5rem",
          fontWeight: 800,
          color: "white",
          lineHeight: 1,
          mb: 1,
        }}
      >
        {formatNullable(derived?.costPerKpiUnit ?? null, formatCurrency)}
      </Typography>
      {unit && (
        <Typography
          sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#81D647" }}
        >
          per {unit}
        </Typography>
      )}
    </Box>
  </Box>
);
